import { makeAutoObservable, runInAction } from "mobx"
import MessageAPI from "../api/message_api"
import { BaseStore } from "./base_store";
import { api_post, api_get } from "api/utils";


export type MessageQAType = {
    cls: string;
    uuid: string;
    collab: string;
    identity: string;
    content: string;
    content_type: string;
    response_text: string;
    response_pass: boolean;
    response_flags: string[];
    date_created: string;
    date_updated: string;
};
export type MessageProps = {
    cls?: string;
	key: string;
    uuid: string;
    identity: string;
	collab?: string;
    request?: string;
    associated_type?: string;
    associated_uuid?: string;
    original_content: string;
	content: string;
	content_plain: string;
	content_type: string;
    context_type: string;
    context_uuid: string;
	date_created: string;
	unread: boolean;
    ignored_qa: string;
	attachment?: {
		type: string;
		fileName: string;
		size: string;
	};
	updateProps: (attributes: MessageProps) => void;
};

// Domain object Message
export class Message implements MessageProps {
    cls = "";
	key  = "";
    uuid = "";
	collab = "";
    request = "";
	identity = "";
    associated_type = "";
    associated_uuid = "";
    original_content = "";
	content = "";
	content_plain = "";
	content_type = "";
    context_type = "";
    context_uuid = "";
	date_created = "";
    ignored_qa = "";
	unread = false;

	constructor(attributes: MessageProps) {
		makeAutoObservable(this, {
			updateProps: false,
            // store: false,
            // autoSave: false,
            // saveHandler: false,
            // dispose: false
		})
		this.updateProps(attributes);
	}

    updateProps(attributes: MessageProps) {
        Object.assign(this, attributes);
    }

}


export class MessagesStore extends BaseStore<MessageProps, Message> {
	LOADED_MESSAGES = [] as string[]
	USE_CACHE = false

    async sendAssociated(
        identity: string,
        associated_type: string,
        associated_uuid: string,
        content: string,
        context_type: string,
        context_uuid: string
    ) {

        return api_post("/message/send/associated",
            {
                identity,
                associated_type,
                associated_uuid,
                content,
                context_type,
                context_uuid
            })
            .then(async (res) => {
                const message = await res.json();
                this.receiveUpdate(message);
                return message;
            })
            .catch((err) => {
                console.log(err);
            });
	}

    async fetchListByAssociation(
        identity: string,
        associated_type: string,
        associated_uuid: string,
        cursor: string | null
    ) {
        return api_get("/message/list/associated",
            {
                identity,
                associated_type,
                associated_uuid,
                cursor: cursor || '',
            })
            .then(async (response) => {
                const res = await response.json();
                this.receiveUpdate(res.items);
                if (res.cursor) {
                    this.fetchListByAssociation(
                        identity,
                        associated_type,
                        associated_uuid,
                        res.cursor);
                }
                return res.items;
            })
            .catch((err) => {
                console.log(err);
            });
    }

    async fetchRecentMessages(
        identity: string,
        associated_type: string,
        uuids: string[],
        cursor: string | null
    ) {
        return api_get("/message/list/associated/recent",
            {
                identity,
                associated_type,
                uuids: uuids.join(","),
                cursor: cursor || '',
            })
            .then(async (response) => {
                const res = await response.json();
                this.receiveUpdate(res.items);
                return res.items;
            })
            .catch((err) => {
                console.log(err);
            });
    }

	async send(collabKey: string,
        identityKey: string,
        content: string,
        ignore_qa = ""
    ) {
		return MessageAPI.send(collabKey, identityKey, content, ignore_qa)
			.then((m) => {
				this.receiveUpdate(m);
				return m;
			})
			.catch((err) => {
				console.log(err);
			});

	}

	async qa(workKey: string, collabKey: string, content: string): Promise<MessageQAType>{
        return api_post("/message/qa", { identity: workKey, collab: collabKey, content })
            .then(async (r) => {
                const message = await r.json();
                return message as MessageQAType;
            });
	}

	fetchMessages(collabKey: string, identityKey: string, cursor  = "", count = 0) {
        if(this.USE_CACHE && this.LOADED_MESSAGES.includes(collabKey)) {
			console.info("already loaded these messages, doing nothing (%s)",
                collabKey);
			return;
        }

        // console.log("fetchMessages: cursor: %s cnt: %d", cursor, count);

        MessageAPI.list(collabKey, identityKey, cursor)
            .then((response) => {
                const { items, new_cursor } = response;
                items.forEach((c : MessageProps) => {
                    // this has to runInAction, because we do it via settimeout below
                    // (for extra pages)
                    runInAction(() => {
                        this.receiveUpdate(c);
                    });
                });

                if(new_cursor) {
                    console.log("fetching more messages, with cursor: %s", cursor);
                    let s = this;
                    setTimeout(function() {
                        s.fetchMessages(collabKey, identityKey, new_cursor, count + 1);
                    }, 100 * count);
                }
                else {
                    this.LOADED_MESSAGES.push(collabKey);
                }
            })
            .catch((err) => {
                console.error("There was an error loading messages");
                console.error(err);
            });

    }

    getMessages(collabKey : string) {
        if(!collabKey) { return []; }
        return this.items.filter(m => m.collab === collabKey).sort((a, b) => {
            return a.date_created > b.date_created ? 1 : -1;
        });
    }

	modelConstructor() {
		return Message;
	}

    forwardTypes() {
        return ['message']
    }

}
